// JOBE-0098.2: pure parse(input) → ParsedQuery for the omni palette.
//
// No React, no DOM, no I/O. Caller strips the leading `␣` mode-flip
// before calling; the parser silently treats it as whitespace.

import {
  FRIENDLY_VOCABULARY,
  FRIENDLY_ALIASES,
  RETIRED_TOKENS,
  findVocabularyByFriendlyToken,
} from './vocabulary.js';
import { splitTokenOp, DATE_TOKENS, stripOpFromWire } from './operators.js';

/**
 * @typedef {':' | '>=' | '<=' | '>' | '<' | '!='} ParsedOp
 *
 * @typedef {Object} ParsedSpan
 * @property {number} start
 * @property {number} end
 *
 * @typedef {Object} ParsedPredicate
 * @property {string} friendlyToken
 * @property {string} rawPredicate
 * @property {ParsedOp} op
 * @property {string[]} values
 * @property {ParsedSpan} span
 *
 * @typedef {Object} UnrecognizedFragment
 * @property {string} text
 * @property {'retired' | 'unknown'} reason
 * @property {ParsedSpan} span
 *
 * @typedef {Object} ParsedQuery
 * @property {ParsedPredicate[]} predicates
 * @property {string | null} fuzzyFragment
 * @property {UnrecognizedFragment[]} unrecognized
 * @property {string} raw
 *
 * @typedef {Object} ParserConfig
 * @property {string[]} [kinds] caller-supplied bare-token kind slugs
 */

const EMPTY_CONFIG = Object.freeze({ kinds: [] });

/**
 * @param {string} input
 * @param {ParserConfig} [config]
 * @returns {ParsedQuery}
 */
export function parse(input, config = EMPTY_CONFIG) {
  const raw = typeof input === 'string' ? input : '';
  const kinds = Array.isArray(config?.kinds) ? config.kinds : [];

  const tokens = tokenize(raw);

  const predicates = [];
  const unrecognized = [];
  const fuzzyParts = [];

  for (const tok of tokens) {
    const { text, span, quoted } = tok;

    if (quoted) {
      fuzzyParts.push(text);
      continue;
    }

    const { token, op, rest } = splitTokenOp(text);

    if (op === null) {
      if (kinds.includes(text)) {
        predicates.push({
          friendlyToken: 'type',
          rawPredicate: 'type',
          op: ':',
          values: [text],
          span,
        });
      } else {
        fuzzyParts.push(text);
      }
      continue;
    }

    if (token === '') {
      fuzzyParts.push(text);
      continue;
    }

    // `q:<value>` is the explicit full-text fragment form. Per spec
    // §Quoted strings, the parser routes its value to fuzzyFragment
    // rather than emitting a predicate; this keeps full-text matching
    // out of the structured-predicate plane.
    if (token === 'q') {
      const vals = splitValues(rest);
      if (vals.length === 0) {
        fuzzyParts.push(text);
      } else {
        for (const v of vals) fuzzyParts.push(v);
      }
      continue;
    }

    if (RETIRED_TOKENS.has(token)) {
      unrecognized.push({ text, reason: 'retired', span });
      continue;
    }

    if (token.startsWith('field.')) {
      const field = token.slice('field.'.length);
      if (field === '') {
        unrecognized.push({ text, reason: 'unknown', span });
        continue;
      }
      predicates.push({
        friendlyToken: token,
        rawPredicate: field,
        op: normalizeOp(op),
        values: splitValues(rest),
        span,
      });
      continue;
    }

    const canonical = FRIENDLY_ALIASES[token] || token;

    if (DATE_TOKENS[canonical]) {
      predicates.push({
        friendlyToken: canonical,
        rawPredicate: DATE_TOKENS[canonical],
        op: normalizeOp(op),
        values: splitValues(rest),
        span,
      });
      continue;
    }

    const vocab = findVocabularyByFriendlyToken(token);
    if (vocab) {
      predicates.push({
        friendlyToken: vocab.friendlyToken,
        rawPredicate: stripOpFromWire(vocab.wirePredicate),
        op: normalizeOp(op),
        values: splitValues(rest),
        span,
      });
      continue;
    }

    unrecognized.push({ text, reason: 'unknown', span });
  }

  const fuzzy = fuzzyParts.join(' ').trim();
  return {
    predicates,
    fuzzyFragment: fuzzy === '' ? null : fuzzy,
    unrecognized,
    raw,
  };
}

function normalizeOp(op) {
  return op === null ? ':' : op;
}

// splitValues: comma-split, but commas inside double-quoted runs are
// preserved. e.g. `"a,b",c` → ['a,b', 'c'].
function splitValues(rest) {
  if (rest === '' || rest === undefined || rest === null) return [];
  const out = [];
  let buf = '';
  let inQuote = false;
  for (let i = 0; i < rest.length; i++) {
    const ch = rest[i];
    if (ch === '"') {
      inQuote = !inQuote;
      buf += ch;
      continue;
    }
    if (ch === ',' && !inQuote) {
      out.push(unquote(buf));
      buf = '';
      continue;
    }
    buf += ch;
  }
  out.push(unquote(buf));
  return out;
}

function unquote(v) {
  if (v.length >= 2 && v.startsWith('"') && v.endsWith('"')) {
    return v.slice(1, -1);
  }
  return v;
}

// tokenize: whitespace-split with a quoted-string sub-walker. Quotes
// inside a token are kept attached to the surrounding token (so
// `q:"vendor obs"` parses as a single token `q:"vendor obs"`).
// Unmatched quotes are best-effort: the run continues to end-of-input.
function tokenize(raw) {
  const tokens = [];
  let i = 0;
  const n = raw.length;
  while (i < n) {
    while (i < n && isWhitespace(raw[i])) i++;
    if (i >= n) break;
    const start = i;
    let inQuote = false;
    let sawQuote = false;
    while (i < n) {
      const ch = raw[i];
      if (inQuote) {
        if (ch === '"') {
          inQuote = false;
        }
        i++;
        continue;
      }
      if (ch === '"') {
        inQuote = true;
        sawQuote = true;
        i++;
        continue;
      }
      if (isWhitespace(ch)) break;
      i++;
    }
    const text = raw.slice(start, i);
    const quoted = sawQuote && text.startsWith('"');
    tokens.push({ text, span: { start, end: i }, quoted });
  }
  return tokens;
}

function isWhitespace(ch) {
  return ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r';
}
